import {
  AttendanceEmployee,
  AttendanceMonth,
  getAttendanceVersions,
  getAvailableAttendanceMonths,
  saveAttendance,
  unsubmitAttendance,
} from "@/api/openapi";
import { AttendancesContext, PspContext } from "@/config/contexts";
import { translate } from "@/config/translator";
import { useContext, useState } from "react";
import { toast } from "react-toastify";
import {
  attendanceToTempAttendance,
  tempAttendanceToAttendance,
} from "../attendanceUtils";

type UseAttendanceDesktopParams = {
  onRestoreEmployee: (employeeMatricola: number) => void;
};

// if you hover the row to add a giustificativo, that is treated as an
// additional giustificativo, so giustificativoIndex === giustificativi.length
// (in other words, one after the last one)
export type HoveredAttendanceCellData = {
  giustificativoIndex: number | null;
  dayIndex: number;
  employeeIndex: number;
};

export function useAttendanceDesktop({
  onRestoreEmployee,
}: UseAttendanceDesktopParams) {
  const { lang, selectedMandateId, user } = useContext(PspContext);
  const {
    selectedMonth,
    setSelectedMonth,
    selectedYear,
    setSelectedYear,
    attendanceMonth,
    setAttendanceMonth,
    setAttendanceVersions,
    availableMonths,
    setAvailableMonths,
    tempAttendanceMonth,
    setTempAttendanceMonth,
    isSubmitted,
    submittedAt,
    setIsSubmitted,
    setSubmittedAt,
  } = useContext(AttendancesContext);
  const canRevokeAttendance =
    user?.scopes.includes("employee") ||
    user?.scopes.includes("admin") ||
    user?.scopes.includes("super_employee");

  const [vociOpenEmployee, setVociOpenEmployee] =
    useState<AttendanceEmployee | null>(null);
  const [vociOpen, setVociOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [restoreEmployeeConfirmationOpen, setRestoreEmployeeConfirmationOpen] =
    useState(false);
  const [wantsToRestoreEmployee, setWantsToRestoreEmployeeMatricola] =
    useState<AttendanceEmployee | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  function updateVersionsList() {
    if (!selectedMandateId) {
      toast(translate("noMandateSelected", lang));
      return;
    }
    if (selectedMonth === undefined || selectedYear === undefined) {
      return;
    }
    getAttendanceVersions({
      query: {
        mandate_id: selectedMandateId,
        month: selectedMonth,
        year: selectedYear,
      },
    }).then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
        return;
      }
      setAttendanceVersions(data);
    });
    getAvailableAttendanceMonths({
      query: { mandate_id: selectedMandateId },
    }).then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
        throw error;
      }
      setAvailableMonths(data);
    });
  }

  function onSaveEdit(isDraft: boolean, attendanceMonth?: AttendanceMonth) {
    if (!selectedMandateId) {
      toast(translate("noMandateSelected", lang));
      return;
    }
    if (!tempAttendanceMonth && !attendanceMonth) {
      toast(translate("somethingWentWrong", lang));
      throw new Error(
        "No tempAttendanceMonth object present even though user tried to save.",
      );
    }
    if (selectedMonth === undefined || selectedYear === undefined) {
      return;
    }
    setIsSaving(true);
    if (!attendanceMonth) {
      setAttendanceMonth(tempAttendanceToAttendance(tempAttendanceMonth!));
    }
    saveAttendance({
      body: {
        mandateId: selectedMandateId,
        data:
          attendanceMonth?.attendances ??
          tempAttendanceToAttendance(tempAttendanceMonth!).attendances,
        month: selectedMonth,
        year: selectedYear,
      },
      query: {
        draft: isDraft,
      },
    }).then(({ data, error }) => {
      setIsSaving(false);
      if (error) {
        toast(translate("somethingWentWrong", lang));
        throw error;
      }

      setAttendanceMonth(data);
      setTempAttendanceMonth(attendanceToTempAttendance(data));
      updateVersionsList();
      if (!isDraft) {
        toast(translate("changesSavedSuccessfully", lang));
      }
    });
  }

  function openVoci(employee: AttendanceEmployee) {
    setVociOpen(true);
    setVociOpenEmployee(employee);
  }

  function closeVoci() {
    setVociOpen(false);
    setTimeout(() => setVociOpenEmployee(null), 250);
  }

  function openRestoreEmployeeDialog(employee: AttendanceEmployee) {
    setRestoreEmployeeConfirmationOpen(true);
    setWantsToRestoreEmployeeMatricola(employee);
  }

  function closeRestoreEmployeeDialog() {
    setRestoreEmployeeConfirmationOpen(false);
    setTimeout(() => {
      setWantsToRestoreEmployeeMatricola(null);
    }, 250);
  }

  function restoreEmployeeWrapper(employeeMatricola: number) {
    onRestoreEmployee(employeeMatricola);
    closeRestoreEmployeeDialog();
  }

  function onSelectMonth(selectedOption: string) {
    // the month and year are stored in a string in the format "<month> <year>"
    const [monthString, yearString] = selectedOption.split(" ");
    const month = Number.parseInt(monthString);
    const year = Number.parseInt(yearString);

    if (Number.isNaN(month) || Number.isNaN(year)) {
      toast(translate("somethingWentWrong", lang));
      throw new Error(
        "The selected month or year or both are not valid integers.",
      );
    }

    setSelectedMonth(month);
    setSelectedYear(year);
  }

  function onRevokeAttendance() {
    if (!attendanceMonth) return;
    unsubmitAttendance({ path: { attendance_id: attendanceMonth.id } }).then(
      ({ data, error }) => {
        if (error) {
          toast(translate("somethingWentWrong", lang));
          return;
        }

        setAttendanceMonth(data.data);
        setTempAttendanceMonth(attendanceToTempAttendance(data.data));
        setIsSubmitted(data.isSubmitted);
        setSubmittedAt(data.submittedAt ? new Date(data.submittedAt) : null);
      },
    );
  }

  return {
    lang,
    onSaveEdit,
    vociOpenEmployee,
    selectedMonth,
    selectedYear,
    vociOpen,
    openVoci,
    closeVoci,
    versionsOpen,
    setVersionsOpen,
    tempAttendanceMonth,
    setTempAttendanceMonth,
    restoreEmployeeConfirmationOpen,
    wantsToRestoreEmployee,
    openRestoreEmployeeDialog,
    closeRestoreEmployeeDialog,
    restoreEmployeeWrapper,
    availableMonths,
    onSelectMonth,
    isSaving,
    isSubmitted,
    submittedAt,
    canRevokeAttendance,
    onRevokeAttendance,
  };
}
